import { useMemo, useState } from 'react';
import { CopyButton } from '@Components/CopyButton';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { Appearance, Size } from '@Components/designConstants';
import { formatPercent } from '../data-qa-utils';
import {
  BrandIndexGroundTruthWeek,
  BrandIndexMetric,
  buildBrandIndexGroundTruthSql,
  computeBrandIndexAggregateDeviation
} from '../brand-index-ground-truth-sql';

interface BrandIndexGroundTruthPanelProps {
  company: string;
  metrics: BrandIndexMetric[];
  weeks: BrandIndexGroundTruthWeek[];
  // dataset.reviewGranularity -- see buildBrandIndexGroundTruthSql's dateTruncUnitFor
  // doc comment for why the canonical-side SQL must bucket by the same grain as `weeks`.
  reviewGranularity?: string;
}

// Reuses formatPercent for the digits+% (rounding, decimal formatting), just adding
// the leading '+' formatPercent itself doesn't -- Justin's ask (2026-08-31): use the
// shared formatter where it fits rather than hand-rolling the whole thing.
const signedPct = (fraction: number) => `${fraction > 0 ? '+' : ''}${formatPercent(fraction, 1)}`;

// The compare-to-canonical (YouGov BrandIndex) layer folded into Dependent Variable's
// combined BrandIndex chart -- same pattern as GqvGroundTruthPanel/Portfolio's
// GroundTruthSqlPanel (an always-visible deviation readout, no fixed pass/fail cutoff,
// plus a collapsible SQL block the reviewer runs themselves), just one deviation line
// per metric instead of a single metric or a spend/impressions pair.
export const BrandIndexGroundTruthPanel = ({
  company,
  metrics,
  weeks,
  reviewGranularity
}: BrandIndexGroundTruthPanelProps) => {
  const [open, setOpen] = useState(false);
  const deviationByMetric = useMemo(
    () => computeBrandIndexAggregateDeviation(weeks, metrics),
    [weeks, metrics]
  );
  const sql = useMemo(
    () => buildBrandIndexGroundTruthSql({ company, metrics, weeks, reviewGranularity }),
    [company, metrics, weeks, reviewGranularity]
  );

  return (
    <div className='data-qa-gt'>
      {deviationByMetric && (
        <div className='data-qa-gt__deviation'>
          MMM vs. canonical (BrandIndex), training window total:{' '}
          {metrics.map(m => `${m.label} ${signedPct(deviationByMetric[m.key] ?? 0)}`).join(' · ')}
        </div>
      )}

      <Button
        appearance={Appearance.EMPTY}
        size={Size.SM}
        className='data-qa-gt__toggle'
        aria-expanded={open}
        leadingElement={open ? <MdExpandLess /> : <MdExpandMore />}
        onClick={() => setOpen(o => !o)}
      >
        Compare to canonical (BrandIndex)
      </Button>

      {open && (
        <div className='data-qa-gt__detail'>
          <div className='data-qa-gt__sql'>
            <div className='data-qa-gt__sql-header'>
              <div className='data-qa-gt__sql-label'>
                Run in Redshift to reproduce the canonical BrandIndex total for each metric/week
                above.
              </div>
              <CopyButton text={sql} aria-label='Copy SQL to clipboard' tooltipLabel='Copy SQL' />
            </div>
            <pre>{sql}</pre>
          </div>
        </div>
      )}
    </div>
  );
};
